import { Component, signal, OnInit } from '@angular/core';
import { AuthService } from 'src/auth/services/auth.service';
import { LocalStorageService } from 'src/shared/services/local-storage.service';
import { PolicySetKey, RouteType, Portal } from 'src/shared/constants';
import { AuthClient, AvailableEdOrgDto, UserPolicyEvalDto } from 'src/shared/services/api.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { EducatorModalComponent } from 'src/global-search/educator-modal/educator-modal.component';
import { LeaStaffModalComponent } from 'src/global-search/lea-staff/lea-staff-modal/lea-staff-modal.component';
import { CertPolicies } from 'src/auth/cert-policies';
import { EdOrgSelectionComponent } from '../ed-org-selection/ed-org-selection.component';
import { BehaviorSubject, EMPTY, filter, from, switchMap } from 'rxjs';
import { NavigationEnd, Router } from '@angular/router';
import { RoleInfoComponent } from '../role-info/role-info.component';
import { RouteTypeService } from 'src/shared/services/route-type.service';
import { JwtService } from 'src/auth/services/jwt.service';
import { ConfigService } from 'src/shared/services/config.service';
import { FeatureManagementService } from 'src/shared/services/feature-management.service';

@Component({
  selector: 'cert-nav-menu',
  templateUrl: './nav-menu.component.html',
  styles: ``,
  standalone: false,
})
export class NavMenuComponent implements OnInit {
  isExpanded = false;
  policySet = JSON.parse(this.localStorageService.get(PolicySetKey) ?? '{}') as UserPolicyEvalDto;
  canSelfAssignServiceItems: boolean = this.authService.hasPolicy(CertPolicies.CanSelfAssignServiceItems);
  edOrgOptions = signal<AvailableEdOrgDto[]>([]);
  currentRouteType = signal<RouteType | undefined>(undefined);
  currentEdOrgName?: string;
  currentEdOrgId?: number;
  fiscalYearDescription?: string;
  routeTypes = RouteType;
  homeUrl = signal('');
  portalRoutes = signal<{ portal: Portal; route: string; description: string }[]>([]);
  edOrgOptionsRefresh$ = new BehaviorSubject<void>(undefined);
  dataLoaded = signal(false);
  obscurityFeatureEnabled = signal(false);
  obscurityFeatureActive = signal(false);

  mergeEducatorsFeatureEnabled$ = this.featureManager.isFeatureEnabled('einConflictResolution');
  credentialConfigurationFeatureEnabled$ = this.featureManager.isFeatureEnabled('credentialConfiguration');
  evalQualPathToolFeatureEnabled$ = this.featureManager.isFeatureEnabled('evaluationQualificationPathTool');
  yearTransitionFeatureEnabled$ = this.featureManager.isFeatureEnabled('leaYearTransition');
  surveyFeatureEnabled$ = this.featureManager.isFeatureEnabled('surveys');
  vouchersEnabled$ = this.featureManager.isFeatureEnabled('vouchers');

  canMapRolePermissions: boolean = this.authService.hasPolicy(CertPolicies.CanMapRolePermissions);
  canManageHangfire: boolean = this.authService.hasPolicy(CertPolicies.CanManageHangfire);
  canManageHelpObjects: boolean = this.authService.hasPolicy(CertPolicies.CanManageHelpObjects);
  canManagePageAlerts: boolean = this.authService.hasPolicy(CertPolicies.CanManagePageAlerts);
  canViewAdminConfigurations: boolean = this.authService.hasPolicy(CertPolicies.CanViewAdminConfigurations);
  canViewAdminReports: boolean = this.authService.hasPolicy(CertPolicies.CanViewAdminReports);
  showAdminDropdown: boolean =
    this.canMapRolePermissions ||
    this.canManageHangfire ||
    this.canManageHelpObjects ||
    this.canManagePageAlerts ||
    this.canViewAdminConfigurations ||
    this.canViewAdminReports;

  environment?: string;

  constructor(
    private readonly authService: AuthService,
    private readonly modalService: NgbModal,
    private readonly localStorageService: LocalStorageService,
    private readonly router: Router,
    private readonly authClient: AuthClient,
    private readonly routeTypeService: RouteTypeService,
    private readonly jwtService: JwtService,
    private readonly configService: ConfigService,
    private readonly featureManager: FeatureManagementService,
  ) {
    this.currentEdOrgName = jwtService.currentEdOrgName;
    this.currentEdOrgId = jwtService.currentEdOrgId;
    this.fiscalYearDescription = configService.fiscalYearDescription;
  }

  ngOnInit() {
    this.featureManager.isFeatureEnabled('informationObscurity').subscribe((isEnabled) => {
      this.obscurityFeatureEnabled.set(isEnabled);
      this.obscurityFeatureActive.set(isEnabled);
    });

    this.getEdOrgOptions();

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.currentRouteType.set(this.routeTypeService.getRouteType());
      if (this.currentRouteType()) {
        this.dataLoaded.set(true);
      }
    });

    this.jwtService.allowedPortals$.subscribe((u) => {
      this.updateAllowedPortals(u);
    });
    this.currentRouteType.set(this.routeTypeService.getRouteType());
    if (this.currentRouteType()) {
      this.dataLoaded.set(true);
    }

    this.environment = this.configService.config.environment;
  }

  getEdOrgOptions() {
    this.edOrgOptionsRefresh$
      .pipe(
        switchMap(() => {
          return this.authClient.getAvailableEdOrgs();
        }),
      )
      .subscribe((x) => {
        this.edOrgOptions.set(x);
      });
  }

  updateAllowedPortals(currentAllowedPortals: string[]): void {
    this.homeUrl.set(this.routeTypeService.getHomeRouteForPortal(currentAllowedPortals));

    this.portalRoutes.set(
      [
        { portal: Portal.Cert, route: this.routeTypeService.certHomeRoute, description: 'Certification' },
        { portal: Portal.Lea, route: this.routeTypeService.leaHomeRoute, description: 'LEA' },
        { portal: Portal.Lea2, route: this.routeTypeService.lea2HomeRoute, description: 'LEA2' },
        { portal: Portal.Epp, route: this.routeTypeService.eppHomeRoute, description: 'EPP' },
      ].filter((x) => currentAllowedPortals.some((t) => t.toLowerCase() === x.portal.toLowerCase())),
    );
  }

  collapse() {
    this.isExpanded = false;
  }

  toggle() {
    this.isExpanded = !this.isExpanded;
  }

  get canChangeEdOrg() {
    return this.edOrgOptions().length > 0;
  }

  onChangeEdOrg(event: Event) {
    event.preventDefault();
    const modalRef = this.modalService.open(EdOrgSelectionComponent, {
      backdrop: 'static',
      size: 'lg',
      centered: true,
    });

    const policySet$ = from(modalRef.result).pipe(
      switchMap((x) => {
        const edOrgId = x as number;
        return this.authService.changeEdOrg(edOrgId);
      }),
    );

    policySet$
      .pipe(
        switchMap((policySet) => {
          if (!policySet) {
            return EMPTY;
          }

          return this.routeTypeService.navigateToUserHomePage({
            forceReload: true,
          });
        }),
      )
      .subscribe();
  }

  onViewProfile(event: Event) {
    event.preventDefault();
    this.modalService.open(RoleInfoComponent, { backdrop: 'static', size: 'lg', centered: true });
  }

  logout(event: Event) {
    event.preventDefault();

    this.authService.logout();
  }

  openEducatorSearch() {
    const component = this.currentRouteType() === RouteType.Lea2 ? LeaStaffModalComponent : EducatorModalComponent;
    const modalRef = this.modalService.open(component, { backdrop: 'static', size: 'xl', centered: true });

    modalRef.result.then((result) => {}).catch((error) => {});
  }

  onToggleObscurity(event: Event) {
    event.preventDefault();
    this.featureManager.toggleInformationObscurityActiveState();
    this.featureManager.features.subscribe((features) => {
      this.obscurityFeatureActive.set(features.informationObscurity);
    });
  }

  showEducatorSearch() {
    return [RouteType.Cert, RouteType.Lea, RouteType.Epp, RouteType.Lea2].some((x) => x === this.currentRouteType());
  }

  getEducatorSearchLabel(): string {
    return [RouteType.Lea2].some((x) => x === this.currentRouteType()) ? 'Staff Search' : 'Educator Search';
  }

  getBackgroundClass() {
    if (this.environment?.toLowerCase() === 'prod') {
      return 'bg-primary-100';
    } else {
      return 'bg-non-prod-banner';
    }
  }
}
